/**
 * LookupCell — renders a `kind: 'lookup'` column cell.
 *
 * Reads the row's association target(s) from the CRM store (no fetch, no
 * completeness gate — targets arrive inline via the list query's
 * `associated_objects` upsert), and renders each target's property value
 * via `PropertyValueLabel`, with an `EntityBadgeGroup`-style static `+N`
 * overflow pill for the rest.
 */

import { useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { PropertyValueLabel } from '../../components/properties/labels';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import { useStoreObjectsMap } from '../../store/hooks';

export interface LookupCellProps {
  object: CrmObject;
  associationTypeName: string;
  targetPropertyName: string;
  orgId?: number;
  targetSchemasByType?: Record<string, Record<string, PropertyTypeSchema>>;
  maxVisible?: number;
}

interface LookupCellEntry {
  id: string;
  value: unknown;
  schema: PropertyTypeSchema;
  /** The target entity's raw `properties` map — required so a formula target
   *  property can resolve its target-entity-type sibling and format correctly. */
  entityProperties?: Record<string, unknown>;
}

export function LookupCell({
  object,
  associationTypeName,
  targetPropertyName,
  orgId,
  targetSchemasByType,
  maxVisible = 1,
}: LookupCellProps): React.ReactElement {
  const targetIds = useMemo(
    () =>
      object.associations
        ?.filter(a => a.association_type_name === associationTypeName)
        .map(a => a.target_entity_id) ?? [],
    [object.associations, associationTypeName]
  );

  const targetsById = useStoreObjectsMap(targetIds);

  const entries = useMemo(() => {
    const result: LookupCellEntry[] = [];
    for (const id of targetIds) {
      const target = targetsById.get(id);
      if (!target) {
        continue;
      }
      const schema =
        targetSchemasByType?.[target.entity_type_name]?.[targetPropertyName];
      // Fail-closed read-access gate: never render a target value we cannot
      // confirm is user-readable. A missing schema (target type/schemas not
      // loaded, or the property removed from the schema) or an internal-read
      // property both render blank rather than leak an internal CRM value — a
      // saved or cached lookup column can reference such a property even though
      // the add-column picker excludes them (buildLookupColumnOptions). Mirrors
      // the backend's `_readable_target_property` gate.
      if (!schema || schema.value_read_access === 'internal') {
        continue;
      }
      const value = getPropertyValue<unknown>(
        target.properties?.[targetPropertyName]
      );
      if (value == null || value === '') {
        continue;
      }
      result.push({
        id,
        value,
        schema,
        entityProperties: target.properties as Record<string, unknown>,
      });
    }
    return result;
  }, [targetIds, targetsById, targetPropertyName, targetSchemasByType]);

  if (entries.length === 0) {
    return <span className='text-fg-quaternary'>—</span>;
  }

  const visible = entries.slice(0, maxVisible);
  const overflow = entries.length - maxVisible;

  return (
    <div className='inline-flex items-center gap-1 min-w-0 flex-nowrap'>
      {visible.map(entry => (
        <PropertyValueLabel
          key={entry.id}
          value={entry.value}
          propertyType={entry.schema}
          entityProperties={entry.entityProperties}
          orgId={orgId}
          fontSize='table'
          truncate
        />
      ))}
      {overflow > 0 && (
        <Badge
          type='pill-color'
          color='gray'
          size='sm'
          className='shrink-0 !py-0 !px-1.5 text-xs'
        >
          +{overflow}
        </Badge>
      )}
    </div>
  );
}
